import { createFileRoute, getRouteApi } from '@tanstack/react-router';
import { createColumnHelper } from '@tanstack/react-table';
import { LANGUAGE_LABELS, type Language } from '@/admin/languages';
import { parseKind } from '@/admin/publishing/kinds';
import { PostsToolbar, useActiveKind } from '@/admin/publishing/PostsToolbar';
import { StatusBadge } from '@/admin/publishing/StatusBadge';
import { createTablePage } from '@/admin/ui/createTablePage';
import { readTableSearch } from '@/admin/ui/tableSearch';
import api from '@/api';
import type { components } from '@/api/qraft/schema';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import i18n from '~/i18n';

type AdminPostSummary = components['schemas']['AdminPostSummary'];
type ContentStatus = components['schemas']['ContentStatus'];

// Optional keys, not undefined values: a required key would force every
// `<Link to="/admin/posts/">` in the console to pass `search`.
type PostsSearch = {
  kind?: ReturnType<typeof parseKind>;
  language?: Language;
  status?: ContentStatus;
  page?: number;
  q?: string;
};

const routeApi = getRouteApi('/admin/posts/');
const LANGUAGE_CODES = Object.keys(LANGUAGE_LABELS) as Language[];

const columnHelper = createColumnHelper<AdminPostSummary>();

const columns = [
  columnHelper.accessor('id', { header: () => i18n.t('admin.common.id') }),
  columnHelper.accessor('title', {
    header: () => i18n.t('admin.common.title'),
  }),
  columnHelper.accessor('language', {
    header: () => i18n.t('admin.common.language'),
    cell: (c) => i18n.t(LANGUAGE_LABELS[c.getValue()]),
  }),
  columnHelper.accessor('status', {
    header: () => i18n.t('admin.common.status'),
    cell: (c) => <StatusBadge status={c.getValue()} />,
  }),
  columnHelper.accessor('category_names', {
    header: () => i18n.t('admin.publishing.columns.categories'),
    cell: (c) => c.getValue().join(', ') || '—',
    enableSorting: false,
  }),
  columnHelper.accessor('published_at', {
    header: () => i18n.t('admin.publishing.columns.date'),
    cell: (c) => new Date(c.getValue()).toLocaleDateString(),
  }),
];

// Rows open the editor, which starts in its read view. Edit state is in the
// URL (`?edit`), as on the other detail pages.
const PostsList = createTablePage<AdminPostSummary, '/admin/posts/$postId'>({
  name: 'admin.nav.posts',
  searchable: true,
  urlState: true,
  useData: (_page, search, extras) => {
    // Cast: the route names this component, so inferring its search is circular.
    const { language, status } = routeApi.useSearch() as PostsSearch;
    const kind = useActiveKind();
    const { me } = useCurrentUser();
    return api.adminListPosts.useQuery(
      {
        query: {
          kind,
          language,
          status,
          search: search || undefined,
          limit: extras.paging.limit,
          offset: extras.paging.offset,
        },
      },
      // Until the viewer is known there is no tab to show, and an unscoped
      // list would span kinds the tier may not see.
      { enabled: Boolean(me) && kind !== undefined },
    );
  },
  columns,
  itemBaseUrl: '/admin/posts/$postId',
  getParams: (row) => ({ postId: String(row.id) }),
  actions: <PostsToolbar />,
});

export const Route = createFileRoute('/admin/posts/')({
  staticData: { minTier: 'FRONT_END_SUPPORT' },
  validateSearch: (s: Record<string, unknown>): PostsSearch => {
    const table = readTableSearch(s);
    const language = LANGUAGE_CODES.find((l) => l === s.language);
    const status =
      s.status === 'draft' || s.status === 'published' ? s.status : undefined;
    return {
      kind: parseKind(s.kind),
      language,
      status,
      page: table.page > 0 ? table.page + 1 : undefined,
      q: table.q || undefined,
    };
  },
  component: PostsList,
});
